import { Cartesian3, Cartographic, JulianDate, Matrix4, Transforms } from "cesium";

/**
 * Cinema camera — a thin layer on top of the smooth follow camera.
 *
 * The follow camera (replayModes.js, "smoothcam") already gives calm motion:
 * it tracks an invisible proxy that sits on the thermal core, so the view never
 * wobbles through circles. What it does NOT do is choose *where* to sit — the
 * offset stays whatever CHASE_OFFSET / the user's mouse left it at.
 *
 * Cinema takes over exactly that. Every frame it looks at a long, strongly
 * forward-biased slice of the track (-PAST_SEC … +FUTURE_SEC around now — the
 * flight is fully known, so "the future" is just more data) and reduces it to
 * ONE number: how much ground the coming stretch covers, measured as the
 * weighted RMS distance of those samples from the pilot ("spread"). Everything
 * follows from it:
 *
 *   spread small (circling in one place) → close, slightly higher angle
 *   spread large (a long run downwind)   → far back, flatter, horizon in frame
 *
 * and the camera sits opposite the weighted centroid of that slice, i.e. it
 * backs off to the side the pilot is *coming from* and leaves the frame open
 * where they are going. Both quantities are averages over ~2 minutes of track,
 * so they drift instead of jumping. There is deliberately no thermal/glide
 * classifier — a classifier has states, and states pop.
 *
 * On top of that, three things keep the *motion* even, which matters far more
 * than the framing at high playback multipliers:
 *  - the camera flies its own line (a critically damped spring, `_dolly`), so
 *    its speed is an averaged version of the pilot's, not a copy of it;
 *  - panning is capped as a ground speed, not just an angular rate;
 *  - the direction is only followed as far as it is actually determined
 *    (DIR_MIN … DIR_SURE), so circling doesn't send the camera into orbit.
 *
 * It runs at the end of ReplayModes._tick (clock.onTick, after Cesium's own
 * EntityView update and after the screen-space camera controller has consumed
 * mouse input), so the camera it sets is the one that gets rendered.
 */

// --- the slice of track the camera reads -------------------------------------
const PAST_SEC = 20; // the past only stabilises the reading…
const FUTURE_SEC = 120; // …the future is what the camera positions itself for
// Must stay well under half a thermal turn (~20 s), or circling aliases into
// the reading: the centroid then wobbles once per circle and the camera swings
// back and forth chasing it. 61 samples over 140 s ≈ one every 2.3 s.
const SAMPLES = 61;
const PAST_WEIGHT = 0.5;
const FAR_WEIGHT = 0.25; // weight of the far end of the future window

// --- how that reading maps to a camera position ------------------------------
// One continuous ramp, no states: spread (m) → distance behind + angle above.
// The ramp ends are taken from measured tracks: over this window, circling or
// standing around reads ≲120 m and a long run downwind reads ≈800 m.
const SPREAD_NEAR = 120; // m — turning in one place
const SPREAD_FAR = 800; // m — a long straight run
const BACK_NEAR = 1250; // m behind the pilot when the flight is tight
const BACK_FAR = 2800; // …and when it is running
const PITCH_NEAR = 30; // ° above the horizon when tight
const PITCH_FAR = 19; // …and when running (flatter, more sky and horizon)

// How sure we are of the direction, from how far off the pilot the centre of
// the coming stretch lies. A pilot circling in one place has a centre only a
// couple of hundred metres away, and the *direction* of such a short vector
// swings around as the window slides — chase it and the camera orbits the
// thermal for no reason. So the pan rate is scaled by this: committed on a
// glide, nearly still while circling.
// It must never reach zero, though: a pilot can ridge-soar for twenty minutes
// with no net direction at all, and a camera that stops steering entirely stays
// wherever it happened to be — measured on such a flight, stuck 172° out, i.e.
// filming the pilot head-on for the whole middle of the flight and only
// recovering once they set off again. The floor keeps it drifting round.
const DIR_MIN = 120; // m — direction barely usable below this
const DIR_SURE = 500; // m — fully committed above this
const DIR_FLOOR = 0.15; // never stop steering completely

// --- speed averaging ---------------------------------------------------------
// The pilot's own speed is wildly uneven — a thermal is nearly stationary, the
// glide out of it is not — and at high playback multipliers a camera bolted
// rigidly to them surges and stalls with every transition. So the camera flies
// its own line, with inertia. The look-at is unaffected, so nothing moves in
// frame; only the distance breathes, and never by more than MAX_DRAG_FRACTION.
const TAU_FOLLOW = 1.8; // s — how much the camera's speed is averaged
const MAX_DRAG_FRACTION = 0.4; // of the camera's distance from the pilot

// Easing time constants, in REAL seconds — playback speed and scrubbing must
// not change how fast the camera moves.
const TAU_HEADING = 2.5;
const TAU_CENTRE = 2; // s — smooths the centroid before its direction is used
const TAU_DIST = 2;

// Hard ceilings on how fast the camera may move, whatever the easing asks for.
// At 20x+ playback the flight itself turns faster than any camera should pan,
// so the shot is allowed to fall behind: the pilot stays centred either way,
// we just end up watching from the side for a while instead of whipping round.
const MAX_PAN_DEG_PER_SEC = 10;
const MAX_DOLLY_M_PER_SEC = 180;
// An angular limit alone is not enough: 10 °/s is a gentle pan at 800 m and a
// 1200 m/s charge at 7 km, so panning is also capped as a ground speed.
const MAX_PAN_M_PER_SEC = 300;

// Sim-time jump (seconds) that means the timeline was scrubbed rather than
// played. Cut straight to the new shot instead of sweeping across the map.
// Note this cannot be detected from how far the target moved: replayModes
// already clamps that to MAX_LAG_M per frame.
const CUT_SIM_SEC = 30;
const TAU_LIFT_UP = 0.8; // climb away from terrain fast…
const TAU_LIFT_DOWN = 5; // …and sink back slowly

// Terrain: the camera must clear the ground under itself AND see over whatever
// ridge lies between it and the pilot, so the whole sight line is sampled.
const MIN_GROUND_CLEARANCE = 120; // m under the camera itself
const SIGHT_MARGIN = 60; // m the sight line clears terrain in between
const SIGHT_SAMPLES = 8;
const MAX_LIFT = 1200; // m — don't go to orbit for a hopeless valley
// Ridges are checked where the pilot will be in a moment too, so the camera is
// already high enough when it gets there instead of climbing out of a hillside.
const SIGHT_AHEAD_SEC = [25, 55];

const scratchTransform = new Matrix4();
const scratchInverse = new Matrix4();
const scratchPosition = new Cartesian3();
const scratchLocal = new Cartesian3();
const scratchWorld = new Cartesian3();
const scratchOffset = new Cartesian3();
const scratchSample = new Cartesian3();
const scratchCarto = new Cartographic();
const scratchSampleCarto = new Cartographic();
const scratchAhead = [new Cartesian3(), new Cartesian3()];
const scratchRigid = new Cartesian3();
const scratchLocalCam = new Cartesian3();
const scratchVel = new Cartesian3();
const scratchStep = new Cartesian3();
const scratchDrag = new Cartesian3();
const scratchDamp = new Cartesian3();
const scratchTime = new JulianDate();
// Reused sample buffer: the window is re-read every frame, so it must not
// allocate (a few thousand Cartesian3 per second would just feed the GC).
const sampleX = new Float64Array(SAMPLES);
const sampleY = new Float64Array(SAMPLES);
const sampleW = new Float64Array(SAMPLES);

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
const clampMagnitude = (v, max) => (v > max ? max : v < -max ? -max : v);
const lerp = (a, b, t) => a + (b - a) * t;
/** Fraction to move this frame for an exponential ease with time constant tau. */
const ease = (dt, tau) => 1 - Math.exp(-dt / tau);
/** Shortest signed angle from b to a, in (-π, π]. */
const angDiff = (a, b) => {
  let d = (a - b) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  else if (d < -Math.PI) d += Math.PI * 2;
  return d;
};

export class CinemaCamera {
  constructor(replayModes) {
    this.rm = replayModes; // owner: gives us the viewer, followEntity and smTarget
    this.enabled = false;

    this.heading = 0; // compass direction the camera sits behind (rad)
    this.centreX = 0; // smoothed centre of the coming stretch, local ENU (m)
    this.centreY = 0;
    this.back = BACK_FAR; // horizontal distance behind the pilot (m)
    this.up = BACK_FAR * Math.tan((PITCH_FAR * Math.PI) / 180); // height above (m)
    this.lift = 0; // extra height currently needed to clear terrain
    this.shot = null; // last window reading, handy when tuning
    this.pos = new Cartesian3(); // where the camera actually is (speed-averaged)
    this.vel = new Cartesian3(); // …and how fast it is travelling
    this._seeded = false;
    this._hasPos = false;
    this._hasCentre = false;
    this._rigidPrev = new Cartesian3();
    this._lastTime = new JulianDate();
    this._hasLastTime = false;
  }

  get viewer() {
    return this.rm.viewer;
  }

  /** Turn cinema on; the first frame seeds from wherever the camera is now. */
  enable() {
    this.enabled = true;
    this._seeded = false;
    this._hasLastTime = false;
    this._hasPos = false;
    this._hasCentre = false;
  }

  disable() {
    this.enabled = false;
  }

  /** Called every clock tick from ReplayModes._tick while cinema is enabled. */
  update(dt) {
    if (!this.enabled) return;
    const entity = this.rm.followEntity;
    const target = this.rm.smTarget;
    if (!entity || !entity.position || !target) return;

    const transform = Transforms.eastNorthUpToFixedFrame(target, undefined, scratchTransform);
    const inverse = Matrix4.inverseTransformation(transform, scratchInverse);

    if (!this._seeded) this._seed(inverse);

    // Scrubbing jumps the clock: cut to the new shot instead of sweeping. Play
    // it forward at any speed and the step stays a fraction of a frame's worth.
    const now = this.viewer.clock.currentTime;
    const jumped = Math.abs(JulianDate.secondsDifference(now, this._lastTime));
    const cut =
      this._hasLastTime &&
      jumped > Math.max(CUT_SIM_SEC, Math.abs(this.viewer.clock.multiplier) * dt * 4);
    JulianDate.clone(now, this._lastTime);
    this._hasLastTime = true;

    const shot = this._readWindow(entity, inverse);
    this.shot = shot;
    if (shot) this._steer(shot, dt, cut);

    this._apply(transform, inverse, target, cut ? 1e3 : dt, cut);
  }

  /**
   * Start from the current view instead of jumping: read the camera's own
   * distance/height/direction relative to the target and ease away from there.
   * Only the direction is taken as-is — distance and height are pulled into the
   * range the shot ramp can reach, because cinema is usually switched on from
   * the default wide view and easing in from 7 km would mean half a minute of
   * the camera sweeping round at the far end of a very long arm.
   */
  _seed(inverse) {
    const camera = this.viewer.scene.camera;
    const local = Matrix4.multiplyByPoint(inverse, camera.positionWC, scratchLocal);
    const horiz = Math.sqrt(local.x * local.x + local.y * local.y);
    if (horiz > 1) this.heading = Math.atan2(-local.x, -local.y);
    this.back = clamp(horiz, BACK_NEAR * 0.6, BACK_FAR * 1.25);
    this.up = clamp(local.z, 150, BACK_FAR * 0.75);
    this.lift = 0;
    this._seeded = true;
  }

  /**
   * Read the coming stretch of flight in the pilot's local east/north/up frame:
   * where its weighted centre lies (→ which way the flight is developing) and
   * how far it ranges from the pilot (→ how much the camera has to take in).
   */
  _readWindow(entity, inverse) {
    const clock = this.viewer.clock;
    const span = PAST_SEC + FUTURE_SEC;
    const step = span / (SAMPLES - 1);

    let aheadCount = 0;
    let n = 0;
    for (let i = 0; i < SAMPLES; i++) {
      const offsetSec = -PAST_SEC + i * step;
      let t = JulianDate.addSeconds(clock.currentTime, offsetSec, scratchTime);
      if (JulianDate.lessThan(t, clock.startTime)) t = clock.startTime;
      else if (JulianDate.greaterThan(t, clock.stopTime)) t = clock.stopTime;
      const p = entity.position.getValue(t, scratchPosition);
      if (!p) continue;
      const local = Matrix4.multiplyByPoint(inverse, p, scratchLocal);
      sampleX[n] = local.x;
      sampleY[n] = local.y;
      // Nearby future counts most; the far end only hints where this is going.
      sampleW[n] =
        offsetSec < 0 ? PAST_WEIGHT : lerp(1, FAR_WEIGHT, offsetSec / FUTURE_SEC);
      n++;

      // Keep the two soonest samples past SIGHT_AHEAD_SEC for the terrain check.
      if (
        aheadCount < SIGHT_AHEAD_SEC.length &&
        offsetSec >= SIGHT_AHEAD_SEC[aheadCount]
      ) {
        Cartesian3.clone(local, scratchAhead[aheadCount]);
        aheadCount++;
      }
    }
    if (n < 5) return null;

    let sumW = 0;
    let cx = 0;
    let cy = 0;
    let spread2 = 0;
    for (let i = 0; i < n; i++) {
      const w = sampleW[i];
      sumW += w;
      cx += w * sampleX[i];
      cy += w * sampleY[i];
      spread2 += w * (sampleX[i] * sampleX[i] + sampleY[i] * sampleY[i]);
    }
    cx /= sumW;
    cy /= sumW;

    return {
      centreX: cx,
      centreY: cy,
      centreDist: Math.sqrt(cx * cx + cy * cy),
      // RMS distance of the slice from the pilot — the pilot is the frame
      // centre, so this is literally "how much has to fit around them".
      spread: Math.sqrt(spread2 / sumW),
      aheadCount, // scratchAhead[0..aheadCount) : where the pilot will be
    };
  }

  /** Ease heading / distance / height toward what the coming stretch wants. */
  _steer(shot, dt, cut) {
    // Sit opposite the centre of the coming stretch, so it opens up ahead.
    // Always follow from behind — a head-on shot on reversals was tried and
    // rejected; a slow pan round to the new side reads better.
    const settle = cut || !this._hasCentre ? 1 : ease(dt, TAU_CENTRE);
    this._hasCentre = true;
    this.centreX += (shot.centreX - this.centreX) * settle;
    this.centreY += (shot.centreY - this.centreY) * settle;
    const centreDist = Math.hypot(this.centreX, this.centreY);
    const sure = clamp((centreDist - DIR_MIN) / (DIR_SURE - DIR_MIN), DIR_FLOOR, 1);

    if (centreDist > 1) {
      const want = Math.atan2(this.centreX, this.centreY);
      const delta = angDiff(want, this.heading);
      const maxPan = Math.min(
        (MAX_PAN_DEG_PER_SEC * Math.PI) / 180,
        MAX_PAN_M_PER_SEC / Math.max(this.back, 1)
      );
      this.heading += cut
        ? delta
        : clampMagnitude(delta * ease(dt, TAU_HEADING) * sure, maxPan * dt);
    }

    const t = clamp((shot.spread - SPREAD_NEAR) / (SPREAD_FAR - SPREAD_NEAR), 0, 1);
    const back = lerp(BACK_NEAR, BACK_FAR, t);
    const up = back * Math.tan((lerp(PITCH_NEAR, PITCH_FAR, t) * Math.PI) / 180);

    if (cut) {
      this.back = back;
      this.up = up;
      return;
    }
    const a = ease(dt, TAU_DIST);
    const maxStep = MAX_DOLLY_M_PER_SEC * dt;
    this.back += clampMagnitude((back - this.back) * a, maxStep);
    this.up += clampMagnitude((up - this.up) * a, maxStep);
  }

  /** Place the camera behind/above the target and look at it. */
  _apply(transform, inverse, target, dt, cut) {
    const offset = Cartesian3.fromElements(
      -Math.sin(this.heading) * this.back,
      -Math.cos(this.heading) * this.back,
      this.up,
      scratchOffset
    );

    // Rise quickly to clear terrain, settle back slowly.
    const need = this._terrainLift(transform, target, offset, this.shot);
    this.lift += (need - this.lift) * ease(dt, need > this.lift ? TAU_LIFT_UP : TAU_LIFT_DOWN);
    offset.z += this.lift;

    // Where a camera bolted to the pilot would be…
    const rigid = Matrix4.multiplyByPoint(transform, offset, scratchRigid);
    // …and where one flying at an averaged speed actually is.
    this._dolly(rigid, Cartesian3.magnitude(offset), dt, cut);

    const local = Matrix4.multiplyByPoint(inverse, this.pos, scratchLocalCam);
    this.viewer.scene.camera.lookAtTransform(transform, local);
  }

  /**
   * Move `this.pos` toward the rigid camera point as a critically damped
   * spring, so the camera has inertia: it accelerates and decelerates over
   * TAU_FOLLOW no matter how abruptly the flight does.
   *
   * Plain easing does not achieve this — its speed is proportional to the
   * error, so a step in the pilot's speed is still a step in the camera's. A
   * velocity low-pass plus a separate catch-up term does not either: the two
   * form an underdamped loop that rings (measured: at 1× it tripled the
   * camera's speed variation). Critical damping is the one that neither
   * overshoots nor oscillates. The lag it leaves is capped at
   * MAX_DRAG_FRACTION, and once pinned there the camera simply travels with
   * the pilot — which is smooth too.
   */
  _dolly(rigid, distance, dt, cut) {
    if (cut || !this._hasPos) {
      Cartesian3.clone(rigid, this.pos);
      Cartesian3.clone(Cartesian3.ZERO, this.vel);
      Cartesian3.clone(rigid, this._rigidPrev);
      this._hasPos = true;
      return;
    }

    const rigidVel = Cartesian3.subtract(rigid, this._rigidPrev, scratchVel);
    Cartesian3.divideByScalar(rigidVel, dt, rigidVel);
    Cartesian3.clone(rigid, this._rigidPrev);

    // acceleration = ω²·(rigid − pos) − 2ω·vel   (ω = 1 / TAU_FOLLOW)
    const w = 1 / TAU_FOLLOW;
    const acc = Cartesian3.subtract(rigid, this.pos, scratchStep);
    Cartesian3.multiplyByScalar(acc, w * w, acc);
    Cartesian3.multiplyByScalar(this.vel, 2 * w, scratchDamp);
    Cartesian3.subtract(acc, scratchDamp, acc);

    Cartesian3.multiplyByScalar(acc, dt, acc);
    Cartesian3.add(this.vel, acc, this.vel);
    Cartesian3.multiplyByScalar(this.vel, dt, scratchStep);
    Cartesian3.add(this.pos, scratchStep, this.pos);

    // Cap the lag; while pinned, travel at the pilot's speed rather than
    // letting the spring wind up against the limit.
    const drag = Cartesian3.subtract(this.pos, rigid, scratchDrag);
    const maxDrag = MAX_DRAG_FRACTION * distance;
    const length = Cartesian3.magnitude(drag);
    if (length > maxDrag) {
      Cartesian3.multiplyByScalar(drag, maxDrag / length, drag);
      Cartesian3.add(rigid, drag, this.pos);
      Cartesian3.clone(rigidVel, this.vel);
    }
  }

  /**
   * How much higher the camera has to sit, in metres, so that it is neither
   * inside the ground nor looking at the pilot through a ridge — now, and where
   * the pilot will be in SIGHT_AHEAD_SEC, so it is already up there in time.
   */
  _terrainLift(transform, target, offset, shot) {
    const targetCarto = Cartographic.fromCartesian(target, undefined, scratchCarto);
    if (!targetCarto) return 0;

    // Sight line at the pilot's current position…
    let neededUp = this._raiseFor(transform, Cartesian3.ZERO, targetCarto.height, offset);
    // …and along the ones coming up. Their ground height differs, so each is
    // measured against its own end point, then expressed as a lift here.
    const ahead = shot ? shot.aheadCount : 0;
    for (let i = 0; i < ahead; i++) {
      const at = scratchAhead[i];
      const world = Matrix4.multiplyByPoint(transform, at, scratchWorld);
      const carto = Cartographic.fromCartesian(world, undefined, scratchSampleCarto);
      if (!carto) continue;
      const up = this._raiseFor(transform, at, carto.height, offset);
      if (up > neededUp) neededUp = up;
    }
    return clamp(neededUp - offset.z, 0, MAX_LIFT);
  }

  /**
   * Camera height (above `endHeight`) needed for a clear line from `end +
   * offset` down to `end`. For a sample at fraction f of the way in, the line
   * sits at endHeight + up·(1-f), so it clears terrain when
   * up ≥ (ground - endHeight) / (1 - f) + margin. The margin is taken at the
   * camera end and tapers to nothing at the pilot, who may legitimately be
   * skimming the ground.
   */
  _raiseFor(transform, end, endHeight, offset) {
    const globe = this.viewer.scene.globe;
    let up = 0;
    for (let i = 0; i < SIGHT_SAMPLES; i++) {
      const f = i / SIGHT_SAMPLES; // 0 = at the camera → 1 = at the pilot
      Cartesian3.multiplyByScalar(offset, 1 - f, scratchSample);
      Cartesian3.add(end, scratchSample, scratchSample);
      const world = Matrix4.multiplyByPoint(transform, scratchSample, scratchWorld);
      const carto = Cartographic.fromCartesian(world, undefined, scratchSampleCarto);
      if (!carto) continue;
      const ground = globe.getHeight(carto);
      if (ground === undefined) continue; // tile not loaded yet — nothing to dodge
      const required =
        (ground - endHeight) / (1 - f) + (i === 0 ? MIN_GROUND_CLEARANCE : SIGHT_MARGIN);
      if (required > up) up = required;
    }
    return up;
  }
}
